import React, { useContext, useEffect, useMemo, useState } from "react";
import { Alert, Button, ButtonGroup, Col, Form, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { SessionContext } from "../SessionProvider";
import reportCardApi, { errorMessage } from "./reportCardApi";
import ClassSectionPicker from "./ClassSectionPicker";
import PageShell from "./PageShell";

const cellKey = (studentId, subjectId) => `${studentId}|${subjectId}`;

/**
 * Route: /reportcard/co-scholastic
 * Grid: students x co-scholastic subjects for one exam title. Enter a grade (A–E) or marks (0–100).
 */
const CoScholasticEntry = () => {
  const { selectedSessionId } = useContext(SessionContext);
  const [filter, setFilter] = useState({ class_id: "", section_id: "" });
  const [examTitleId, setExamTitleId] = useState("");
  const [examTitles, setExamTitles] = useState([]);
  const [mode, setMode] = useState("GRADE"); // GRADE | MARKS
  const [data, setData] = useState({ students: [], subjects: [] });
  const [cells, setCells] = useState({});
  const [gradeOptions, setGradeOptions] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedSessionId) return;
    reportCardApi.examTitles(selectedSessionId).then(setExamTitles).catch(() => setExamTitles([]));
  }, [selectedSessionId]);

  useEffect(() => {
    if (!filter.class_id) return setGradeOptions([]);
    reportCardApi.gradeOptions(filter.class_id, "CO_SCHOLASTIC").then(setGradeOptions).catch(() => setGradeOptions([]));
  }, [filter.class_id]);

  const ready = filter.class_id && filter.section_id && examTitleId && selectedSessionId;

  const load = async () => {
    if (!ready) return;
    setLoading(true);
    try {
      const res = await reportCardApi.coScholasticResults.get({ ...filter, sessionid: selectedSessionId, exam_title_id: examTitleId });
      setData({ students: res.students || [], subjects: res.subjects || [] });
      const c = {};
      (res.results || []).forEach((r) => {
        c[cellKey(r.student_addmission_id, r.co_scholastic_subject_id)] = {
          grade: r.grade || "",
          obtained_marks: r.obtained_marks === null || r.obtained_marks === undefined ? "" : Number(r.obtained_marks),
        };
      });
      setCells(c);
      // open in marks mode if the saved data is marks
      if ((res.results || []).some((r) => r.obtained_marks !== null && !r.grade)) setMode("MARKS");
      setDirty(false);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, [filter.class_id, filter.section_id, examTitleId, selectedSessionId]);

  const setCell = (sid, subId, value) => {
    const k = cellKey(sid, subId);
    setCells((c) => ({
      ...c,
      [k]: mode === "GRADE" ? { grade: value, obtained_marks: "" } : { grade: "", obtained_marks: value },
    }));
    setDirty(true);
  };

  // fill one subject column for everyone (e.g. all "A")
  const fillColumn = (subId, value) => {
    if (!value) return;
    setCells((c) => {
      const next = { ...c };
      data.students.forEach((s) => {
        next[cellKey(s.student_addmission_id, subId)] =
          mode === "GRADE" ? { grade: value, obtained_marks: "" } : { grade: "", obtained_marks: value };
      });
      return next;
    });
    setDirty(true);
  };

  const invalid = useMemo(() => {
    if (mode !== "MARKS") return 0;
    return Object.values(cells).filter((c) => c.obtained_marks !== "" && (isNaN(Number(c.obtained_marks)) || c.obtained_marks < 0 || c.obtained_marks > 100)).length;
  }, [cells, mode]);

  const save = async () => {
    if (invalid) return toast.error("Marks must be between 0 and 100");
    const records = [];
    data.students.forEach((s) =>
      data.subjects.forEach((sub) => {
        const c = cells[cellKey(s.student_addmission_id, sub.id)] || {};
        records.push({
          student_addmission_id: s.student_addmission_id,
          co_scholastic_subject_id: sub.id,
          grade: c.grade || null,
          obtained_marks: c.obtained_marks === "" || c.obtained_marks === undefined ? null : Number(c.obtained_marks),
        });
      })
    );
    try {
      const res = await reportCardApi.coScholasticResults.save({ exam_title_id: examTitleId, sessionid: selectedSessionId, records });
      toast.success(res.message || "Saved");
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const gradeFor = (marks) => {
    if (marks === "" || marks === undefined || isNaN(Number(marks))) return "";
    const hit = [...gradeOptions].sort((a, b) => b.from - a.from).find((g) => Number(marks) >= g.from);
    return hit ? hit.grade : "";
  };

  const selectedTitle = examTitles.find((t) => t.id === examTitleId);

  return (
    <PageShell title="Co-Scholastic Grades">
      <Row className="g-2 mb-3 align-items-end">
        <ClassSectionPicker value={filter} onChange={setFilter} md={3} />
        <Col md={3}>
          <Form.Label className="form-view-label">Exam</Form.Label>
          <Form.Select value={examTitleId} onChange={(e) => setExamTitleId(e.target.value)}>
            <option value="">-- Select Exam --</option>
            {examTitles.map((t) => (
              <option key={t.id} value={t.id}>{t.name}{t.exam_group_name ? ` (${t.exam_group_name})` : ""}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label className="form-view-label d-block">Enter as</Form.Label>
          <ButtonGroup size="sm">
            <Button variant={mode === "GRADE" ? "primary" : "outline-primary"} onClick={() => setMode("GRADE")}>Grade</Button>
            <Button variant={mode === "MARKS" ? "primary" : "outline-primary"} onClick={() => setMode("MARKS")}>Marks (0–100)</Button>
          </ButtonGroup>
        </Col>
      </Row>

      {!selectedSessionId && <Alert variant="warning">Select a session in the header first.</Alert>}
      {ready && !loading && data.subjects.length === 0 && (
        <Alert variant="info">No co-scholastic subjects for this class. Add them in Report Card Settings → Co-Scholastic Subjects.</Alert>
      )}
      {ready && gradeOptions.length === 0 && (
        <Alert variant="warning">No co-scholastic grade scale found. Add one in Report Card Settings → Grade Scales (Type: Co-Scholastic).</Alert>
      )}

      {ready && data.subjects.length > 0 && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted">
              {data.students.length} student(s){selectedTitle?.exam_group_name ? ` · printed under ${selectedTitle.exam_group_name}` : ""}
            </small>
            <Button size="sm" onClick={save} disabled={!dirty}>Save</Button>
          </div>
          <div style={{ overflowX: "auto" }}>
            <Table bordered size="sm" className="align-middle">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Roll</th>
                  <th style={{ minWidth: 180 }}>Student</th>
                  {data.subjects.map((sub) => (
                    <th key={sub.id} style={{ minWidth: 150 }}>
                      {sub.name}
                      <Form.Select size="sm" className="mt-1" value="" onChange={(e) => fillColumn(sub.id, e.target.value)}
                        style={{ display: mode === "GRADE" ? "block" : "none" }}>
                        <option value="">Fill all…</option>
                        {gradeOptions.map((g) => <option key={g.grade} value={g.grade}>{g.grade}</option>)}
                      </Form.Select>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.students.map((s) => (
                  <tr key={s.student_addmission_id}>
                    <td>
                      <Form.Control
                        size="sm"
                        style={{ width: 60, textAlign: "center", padding: "2px 4px", fontSize: "12px" }}
                        placeholder="–"
                        defaultValue={s.roll_no || ""}
                        onBlur={async (e) => {
                          const val = e.target.value.trim();
                          if (val !== (s.roll_no || "")) {
                            try {
                              await reportCardApi.updateRollNo(s.student_addmission_id, val);
                              s.roll_no = val;
                              toast.success(`Roll No updated for ${s.student_name}`);
                            } catch (err) {
                              toast.error(errorMessage(err));
                              e.target.value = s.roll_no || "";
                            }
                          }
                        }}
                      />
                    </td>
                    <td>{s.student_name}</td>
                    {data.subjects.map((sub) => {
                      const c = cells[cellKey(s.student_addmission_id, sub.id)] || {};
                      return (
                        <td key={sub.id}>
                          {mode === "GRADE" ? (
                            <Form.Select size="sm" value={c.grade || (c.obtained_marks !== "" ? gradeFor(c.obtained_marks) : "")}
                              onChange={(e) => setCell(s.student_addmission_id, sub.id, e.target.value)}>
                              <option value="">—</option>
                              {gradeOptions.map((g) => <option key={g.grade} value={g.grade}>{g.grade}</option>)}
                            </Form.Select>
                          ) : (
                            <div className="d-flex align-items-center gap-2">
                              <Form.Control size="sm" type="number" min={0} max={100} step="0.5" style={{ width: 80 }}
                                value={c.obtained_marks ?? ""}
                                isInvalid={c.obtained_marks !== "" && c.obtained_marks !== undefined && (c.obtained_marks < 0 || c.obtained_marks > 100)}
                                onChange={(e) => setCell(s.student_addmission_id, sub.id, e.target.value === "" ? "" : Number(e.target.value))} />
                              <b className="text-muted">{gradeFor(c.obtained_marks) || (c.grade ? c.grade : "")}</b>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {data.students.length === 0 && (
                  <tr><td colSpan={2 + data.subjects.length} className="text-center text-muted">No active students in this section</td></tr>
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </PageShell>
  );
};

export default CoScholasticEntry;
